import React, { useRef } from 'react';
import { useObservable } from 'react-use-observable';

import { Button } from 'antd';

import isAfter from 'date-fns/isAfter';
import startOfDay from 'date-fns/startOfDay';
import { Form as FormikForm, Field, useFormikContext } from 'formik';
import type { FieldProps } from 'formik';
import { FilterActions } from 'v2/components/Pages/Reports/FilterActions';
import { FilterPanel } from 'v2/components/Pages/Reports/FilterPanel';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import type { ISelectProductMultipleHandles } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import useGetUser from 'v2/hooks/useGetUser';
import { EDateTypes } from 'v2/interfaces/reports/refunds';
import type { IRefundFilter } from 'v2/interfaces/reports/refunds';
import { initialFilter, refundsService } from 'v2/services/reports/refunds';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';

import { Icon } from '@eduzz/ui-antd-theme';

interface IMotive {
  id?: string;
  description: string;
}

const dateTypes = [
  { label: 'Pagamento', value: EDateTypes.PAID },
  { label: 'Reembolso', value: EDateTypes.REFUNDED }
];

export const Form = () => {
  const user = useGetUser();

  const [motives, motivesError] = useObservable<IMotive[]>(
    () => refundRequestFactory.getRefundMotivesFromBoApi(user?.id),
    []
  );

  const selectProductRef = useRef<ISelectProductMultipleHandles>(null);

  const { isValid, isSubmitting, resetForm, dirty } = useFormikContext<IRefundFilter>();

  const [isLoading] = useObservable(() => refundsService.isLoading(), []);

  const isOutsideRange = (day: Date) => isAfter(startOfDay(day), startOfDay(new Date()));

  const handleClean = () => {
    resetForm({ values: initialFilter });
    selectProductRef.current?.unCheckAll();
  };

  const motivesOptions = (motives || []).map(item => ({
    label: item.description,
    value: item.id
  }));

  const loadingMotivesOptions = motivesError === null && motives === null;
  const isCleanDisabled = !dirty || isSubmitting || isLoading;
  const isFilterDisabled = !isValid || isSubmitting || isLoading;

  return (
    <FormikForm>
      <FilterPanel id='refunds-filter'>
        <div className='grid grid-cols-1 items-end gap-4 sm:grid-cols-2 md:grid-cols-12'>
          <div className='md:col-span-3'>
            <Field
              id='date_type'
              name='date_type'
              label='Tipo de data'
              options={dateTypes}
              component={SelectFormField}
              fullWidth
            />
          </div>
          <div className='md:col-span-5'>
            <Field
              id='date'
              label='Período'
              name='date'
              component={DateRangePickerFormField}
              isPresetLonger
              isOutsideRange={isOutsideRange}
            />
          </div>
          <div className='md:col-span-4'>
            <Field
              id='invoice_id'
              name='invoice_id'
              label='Fatura'
              placeholder='Número da fatura'
              mask='number'
              component={TextFormField}
              fullWidth
            />
          </div>
          <div className='md:col-span-5'>
            <Field id='product_id' name='product_id'>
              {(props: FieldProps) => (
                <SelectProduct
                  single={false}
                  {...props}
                  label='Produtos'
                  endpoint='/reports/sales-filter-products'
                  ref={selectProductRef}
                />
              )}
            </Field>
          </div>
          <div className='md:col-span-3'>
            <Field
              id='motive_id'
              name='motive_id'
              label='Motivo'
              placeholder='Selecione'
              loading={loadingMotivesOptions}
              options={motivesOptions}
              component={SelectFormField}
              multiple
              fullWidth
            />
          </div>
          <div className='md:col-span-4'>
            <FilterActions>
              <Button id='clean-filter-button' onClick={handleClean} type='text' disabled={isCleanDisabled}>
                Limpar
              </Button>
              <Button
                id='filter-button'
                type='primary'
                disabled={isFilterDisabled}
                htmlType='submit'
                loading={isSubmitting || isLoading}
                icon={<Icon.Funnel />}
              >
                Filtrar
              </Button>
            </FilterActions>
          </div>
        </div>
      </FilterPanel>
    </FormikForm>
  );
};
